import React, {useEffect, useMemo, useState} from 'react';
import tradingService from '../services/tradingService';
import {isTruthy} from '../utils/boolean';

const isCanceledRequest = (err) => {
  if (!err) return false;
  return (
    err?.name === 'AbortError' || // fetch / some libs
    err?.name === 'CanceledError' || // axios v1
    err?.code === 'ERR_CANCELED' || // axios v1
    String(err?.message || '').toLowerCase() === 'canceled'
  );
};

export default function AccountSubscriptionsSection({ number }) {
  const [subsLoading, setSubsLoading] = useState(false);
  const [subsError, setSubsError] = useState('');
  const [subscriptions, setSubscriptions] = useState([]);
  const [subsCount, setSubsCount] = useState(0);
  const [subsLimit, setSubsLimit] = useState(10);
  const [subsOffset, setSubsOffset] = useState(0);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [deactivatingSubId, setDeactivatingSubId] = useState('');

  const subsPage = useMemo(
    () => Math.floor(subsOffset / Math.max(1, subsLimit)) + 1,
    [subsOffset, subsLimit]
  );
  const subsTotalPages = useMemo(
    () => Math.max(1, Math.ceil((subsCount || 0) / Math.max(1, subsLimit))),
    [subsCount, subsLimit]
  );

  const subsCanPrev = subsOffset > 0;
  const subsCanNext = subsOffset + subsLimit < (subsCount || 0);

  useEffect(() => {
    if (!number) return;

    const ac = new AbortController();
    let alive = true;

    (async () => {
      try {
        setSubsLoading(true);
        setSubsError('');

        const res = await tradingService.accounts.subscriptions.listForAccount({
          number,
          limit: subsLimit,
          offset: subsOffset,
          signal: ac.signal,
        });

        if (!alive) return;

        // tradingService returns already "data", not an axios response
        const payload = res ?? {};
        const list = payload?.data?.subscriptions ?? [];
        setSubscriptions(Array.isArray(list) ? list : []);
        setSubsCount(Number(payload?.data?.count ?? 0));
      } catch (err) {
        if (!alive || isCanceledRequest(err) || ac.signal.aborted) return;

        setSubscriptions([]);
        setSubsCount(0);
        setSubsError(err?.message || 'Failed to load subscriptions');
      } finally {
        if (!alive) return;
        setSubsLoading(false);
      }
    })();

    return () => {
      alive = false;
      ac.abort();
    };
  }, [number, subsLimit, subsOffset, reloadNonce]);

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'number') return String(value);

    if (typeof value === 'string') {
      const s = value.trim();
      const sci = /^-?\d+(\.\d+)?e[+-]?\d+$/i;
      if (sci.test(s)) {
        const n = Number(s);
        if (!Number.isFinite(n)) return s;
        const fixed = n.toFixed(8);
        return fixed.replace(/\.?0+$/, '');
      }
      return s;
    }

    return String(value);
  };

  const deactivateSubscription = async (sub) => {
    const subShortId = sub?.short_id;
    if (!number || !subShortId) return;

    const strategyName = sub?.trading_strategy?.name || 'this strategy';
    const ok = window.confirm(`Deactivate subscription for "${strategyName}"?`);
    if (!ok) return;

    try {
      setSubsError('');
      setDeactivatingSubId(String(subShortId));

      await tradingService.accounts.subscriptions.updateForAccount({
        number,
        subShortId,
        is_active: 0,
      });

      // Refresh current page
      setReloadNonce((n) => n + 1);
    } catch (err) {
      if (isCanceledRequest(err)) return;
      setSubsError(err?.message || 'Failed to deactivate subscription');
    } finally {
      setDeactivatingSubId('');
    }
  };

  return (
    <section className="account-details__section">
      <div className="account-details__section-header">
        <div>
          <div className="account-details__section-title">Subscriptions</div>
          <div className="account-details__section-subtitle">
            {subsCount ? (
              <>
                Showing {subsOffset + 1}–{Math.min(subsOffset + subsLimit, subsCount)} of {subsCount}
              </>
            ) : (
              'Subscriptions for this account.'
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 12, opacity: 0.8 }}>Page size</span>
            <select
              value={String(subsLimit)}
              onChange={(e) => {
                const next = Math.max(1, Number(e.target.value) || 10);
                setSubsOffset(0);
                setSubsLimit(next);
              }}
              className="account-details__control"
              style={{ width: 110 }}
              disabled={subsLoading}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
          </label>

          <button
            type="button"
            className="account-details__btn"
            onClick={() => setSubsOffset((o) => Math.max(0, o - subsLimit))}
            disabled={!subsCanPrev || subsLoading}
          >
            Prev
          </button>

          <button
            type="button"
            className="account-details__btn"
            onClick={() => setSubsOffset((o) => o + subsLimit)}
            disabled={!subsCanNext || subsLoading}
          >
            Next
          </button>

          <div style={{ fontSize: 12, opacity: 0.8 }}>
            Page {subsPage} / {subsTotalPages}
          </div>

          <button
            type="button"
            className="account-details__btn"
            onClick={() => setReloadNonce((n) => n + 1)}
            disabled={subsLoading}
            title="Reload current page"
          >
            Refresh
          </button>
        </div>
      </div>

      {subsError ? <div className="account-details__error">{subsError}</div> : null}
      {subsLoading ? <div>Loading…</div> : null}

      {!subsLoading && !subsError ? (
        subscriptions.length ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left' }}>
                  <th style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>Strategy</th>
                  <th style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>Volume limit</th>
                  <th style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>Active</th>
                  <th style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>Subscription ID</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map((s, idx) => {
                  const active = isTruthy(s?.is_active);
                  const busy =
                    String(deactivatingSubId) && String(deactivatingSubId) === String(s?.short_id);

                  // allow only Active -> Inactive (one-way)
                  const canToggleOff = active && !subsLoading && !busy;

                  const tryDeactivateFromToggle = (e) => {
                    // prevent label default / page scroll / etc
                    e.preventDefault();
                    e.stopPropagation();

                    if (!canToggleOff) return;
                    deactivateSubscription(s);
                  };

                  return (
                    <tr key={`${s?.short_id ?? s?.id ?? 'sub'}:${idx}`}>
                      <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                          <span>{s?.trading_strategy?.name ?? '—'}</span>
                        </div>
                      </td>

                      <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>
                        {formatNumber(s?.volume_limit)}
                      </td>

                      <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>
                        <label
                          className={`account-details__toggle ${!canToggleOff ? 'is-disabled' : ''}`}
                          role={canToggleOff ? 'button' : undefined}
                          tabIndex={canToggleOff ? 0 : -1}
                          aria-disabled={!canToggleOff}
                          title={
                            active
                              ? busy
                                ? 'Deactivating…'
                                : 'Click to deactivate (one-way)'
                              : 'Inactive'
                          }
                          onClick={tryDeactivateFromToggle}
                          onKeyDown={(e) => {
                            if (!canToggleOff) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              tryDeactivateFromToggle(e);
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={active}
                            readOnly
                            disabled={!canToggleOff}
                            aria-label="Subscription active"
                          />
                          <span className="account-details__toggle-ui" aria-hidden="true" />
                        </label>
                      </td>

                      <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>
                        {s?.short_id ? <code title="Subscription short id">{s.short_id}</code> : <code>{s?.id ?? '—'}</code>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div>No subscriptions found.</div>
        )
      ) : null}
    </section>
  );
}
